import { Menu as MuiMenu, menuClasses as MuiMenuClasses, MenuProps as MuiMenuProps } from '@mui/material';
import React, { forwardRef } from 'react';
import { CommonDataProps, SxProps } from '../../utils';

type NativeDivProps = React.ComponentPropsWithoutRef<'div'>;

export interface MenuProps
  extends NativeDivProps,
    CommonDataProps,
    Pick<
      MuiMenuProps,
      'anchorOrigin' | 'transformOrigin' | 'anchorReference' | 'anchorPosition' | 'PaperProps' | 'MenuListProps'
    > {
  /**
   * The list of MenuItem components contained within the Menu
   */
  children: React.ReactNode;
  /**
   * Whether the menu is open or not. The Menu component is a
   * controlled component and does not handle any state internally
   */
  open: boolean;
  /**
   * The anchor element used to position the menu
   */
  anchorEl?: Element | HTMLButtonElement | HTMLAnchorElement | null;
  /**
   * Optional aria-label for the menu
   */
  ariaLabel?: string;
  /**
   * Optional aria-labelledby for the menu
   */
  ariaLabelledBy?: string;

  /**
   * Always keep the children in the DOM. This prop can be useful in cases where something inside the menu needs to
   * stay mounted when the menu is closed (e.g. a `Drawer`).
   *
   * This prop is prefixed with "dangerously" because its use often indicates antipatterns that could lead to
   * performance issues or other problems. For example, the use case with the `Drawer` would be better handled by
   * separating the `MenuItem` that triggers a drawer from the `Drawer` component and not nesting the `Drawer` inside
   * the `Menu`.
   *
   * ChronoUI primarily supports this property to ease transitioning to some new responsive patterns for teams.
   */
  dangerouslyKeepMounted?: MuiMenuProps['keepMounted'];
  /**
   * An optional callback that fires when the menu pop-up closes
   */
  onClose?: (event: React.SyntheticEvent, reason: 'backdropClick' | 'escapeKeyDown') => void;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

export const menuClasses = {
  paper: MuiMenuClasses.paper,
};

export const Menu = forwardRef<HTMLDivElement, MenuProps>(function Menu(
  { ariaLabel, ariaLabelledBy, children, dangerouslyKeepMounted, MenuListProps, open, sx, ...props },
  ref
) {
  return (
    <MuiMenu
      {...props}
      open={open}
      MenuListProps={{
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        ...(MenuListProps ?? {}),
      }}
      keepMounted={dangerouslyKeepMounted}
      sx={sx}
      ref={ref}
    >
      {children}
    </MuiMenu>
  );
});
